import React, { useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../ds";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { EventsTable, ContactsTable, OverdueTable, Pipeline } from "../components/afg/AfgViews";
import { AfgLeadDrawer } from "../components/afg/AfgLeadDrawer";

const useAfg = (key, path, params) => useQuery({
  queryKey: ["afg", key, params],
  queryFn: async () => (await api.get(path, { params })).data,
  placeholderData: keepPreviousData,
  refetchInterval: 60000,
});

export default function Afg() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [open, setOpen] = useState(null);
  const p = { q: q || undefined, include_test: includeTest };
  const summary = useAfg("summary", "/afg/summary", { include_test: includeTest }).data;
  const leads = useAfg("leads", "/afg/leads", p).data;
  const inbox = useAfg("in", "/afg/events", { ...p, direction: "in" }).data;
  const sent = useAfg("out", "/afg/events", { ...p, direction: "out" }).data;
  const contacts = useAfg("contacts", "/afg/contacts", p).data;
  const overdue = useAfg("overdue", "/afg/overdue", { include_test: includeTest }).data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["afg"] });
  const tab = (v, label, n) => (
    <TabsTrigger value={v} className="rounded-[4px] focus-visible:ring-2 focus-visible:ring-[color:var(--cobalt)]" data-testid={`afg-tab-${v}`}>
      {label}{n != null ? ` (${n})` : ""}
    </TabsTrigger>
  );

  return (
    <div className="space-y-6" data-testid="afg-page">
      <PageHeader title="AFG Dubai" subtitle="Website leads, mails and quote timing from afgdubai.ae. Display only: Office never sends mail here." />
      <div className="flex flex-wrap items-center gap-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by reference (AFG-DXB-2026-001), name or company" className="max-w-md rounded-[4px] focus-visible:ring-2 focus-visible:ring-[color:var(--cobalt)]" data-testid="afg-search" />
        <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.08em] text-[color:var(--muted)]">
          <Switch checked={includeTest} onCheckedChange={setIncludeTest} data-testid="afg-include-test" /> Show test data
        </label>
      </div>
      <Tabs defaultValue="pipeline">
        <TabsList className="rounded-none">
          {tab("inbox", "Inbox", summary?.inbox)}
          {tab("sent", "Sent", summary?.sent)}
          {tab("pipeline", "Lead pipeline", summary?.total)}
          {tab("contacts", "Contacts", summary?.contacts)}
          {tab("overdue", "Overdue", summary?.overdue)}
        </TabsList>
        <TabsContent value="inbox"><EventsTable events={inbox?.events || []} onOpen={setOpen} testId="afg-inbox-table" /></TabsContent>
        <TabsContent value="sent"><EventsTable events={sent?.events || []} onOpen={setOpen} testId="afg-sent-table" /></TabsContent>
        <TabsContent value="pipeline"><Pipeline leads={leads?.leads || []} stages={leads?.stages || []} onOpen={setOpen} /></TabsContent>
        <TabsContent value="contacts"><ContactsTable contacts={contacts?.contacts || []} onOpen={setOpen} /></TabsContent>
        <TabsContent value="overdue"><OverdueTable leads={overdue?.leads || []} onOpen={setOpen} /></TabsContent>
      </Tabs>
      <AfgLeadDrawer refId={open} includeTest={includeTest} onClose={() => setOpen(null)} onChanged={refresh} />
    </div>
  );
}
